import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import {
  AdminListCard,
  AdminListTable,
  ConfirmDeleteModal,
  PageTabs,
  saveErrorReason,
  useBatchSelection,
  withFailureReasons,
} from "@components/CatalogIndex";
import type { BatchFailure } from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useVendorsList, useVendorMutations } from "../api";
import { buildVendorBatchActions, type VendorTab } from "./batchActions";
import type { Vendor } from "../types";

const PAGE_SIZE = 25;

const formatAddress = (v: Vendor): string => {
  const line1 = [v.streetAddress, v.street2].filter(Boolean).join(", ");
  const line2 = [v.city, v.state].filter(Boolean).join(", ");
  return [line1, line2, v.postalCode].filter(Boolean).join(", ");
};

const List = (): JSX.Element => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<VendorTab>("active");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [searchInput, setSearchInput] = useState("");
  const [nameFilter, setNameFilter] = useState("");

  const sel = useBatchSelection([tab]);
  const { archive, restore } = useVendorMutations();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);

  const { rows, pageMeta, isLoading, isFetching, refetch } = useVendorsList({
    page: { number: pageNumber, size: pageSize },
    filter: {
      active: tab === "active",
      ...(nameFilter ? { name: nameFilter } : {}),
    },
  });

  const selectedVendors = useMemo(
    () => rows.filter((r) => sel.selectedIds.includes(r.id)),
    [rows, sel.selectedIds]
  );

  const columns: ColumnDef<Vendor>[] = [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => row.original.name,
    },
    {
      accessorKey: "address",
      header: "Address",
      cell: ({ row }) => formatAddress(row.original),
    },
    {
      accessorKey: "projectCount",
      header: "Number of Projects",
      cell: ({ row }) => row.original.projectCount || "",
    },
  ];

  const runOnSelected = async (
    fn: (v: Vendor) => Promise<unknown>,
    successText: (n: number) => string
  ) => {
    const targets = [...selectedVendors];
    const failures: BatchFailure[] = [];
    for (const v of targets) {
      try {
        await fn(v);
      } catch (err) {
        failures.push({ label: v.name, reason: saveErrorReason(err) });
      }
    }
    setToast(
      failures.length === 0
        ? { variant: "success", text: successText(targets.length) }
        : {
            variant: "danger",
            text: withFailureReasons(
              `Failed on ${failures.length} of ${targets.length} vendor(s).`,
              failures
            ),
          }
    );
    sel.clear();
    refetch();
  };

  const handleConfirmDeleteOrRestore = async () => {
    setConfirmOpen(false);
    if (tab === "active") {
      await runOnSelected(
        (v) => archive(v.id),
        (n) => `${n} vendor(s) archived.`
      );
    } else {
      await runOnSelected(
        (v) => restore(v.id, v.name),
        (n) => `${n} vendor(s) restored.`
      );
    }
  };

  const batchActions = buildVendorBatchActions(tab, sel.selectedIds.length, {
    onDelete: () => setConfirmOpen(true),
    onRestore: () => setConfirmOpen(true),
  });

  const submitSearch = () => {
    setPageNumber(1);
    setNameFilter(searchInput);
  };

  if (isLoading) return <ScopeStackSpinner />;

  return (
    <>
      <AdminListCard
        title="Vendors"
        onNew={() => navigate("new")}
        newButtonLabel="+ Add A Vendor"
        searchProps={{
          label: "Search Vendors",
          placeholder: "Search by name",
          value: searchInput,
          onChange: (e) => setSearchInput(e.target.value),
          onSubmit: (e) => {
            e.preventDefault();
            submitSearch();
          },
          onClear: () => {
            setSearchInput("");
            setPageNumber(1);
            setNameFilter("");
          },
        }}
        batchActions={batchActions}
        selectedCount={sel.selectedIds.length}
      >
        <PageTabs
          activeKey={tab}
          onSelect={(key) => {
            setTab(key as VendorTab);
            setPageNumber(1);
            sel.clear();
          }}
          tabs={[
            {
              key: "active",
              label: "Active",
              count: tab === "active" ? pageMeta.recordCount : undefined,
            },
            {
              key: "archived",
              label: "Archived",
              count: tab === "archived" ? pageMeta.recordCount : undefined,
            },
          ]}
        />
        <AdminListTable
          columns={columns}
          data={rows}
          isLoading={isFetching}
          selectedIds={sel.selectedIds}
          setSelectedIds={sel.setSelectedIds}
          onEdit={(id) => navigate(`${id}/edit`)}
          emptyMessage="No vendors found."
          paginationEnabled
          pageSizeEnabled
          totalPages={pageMeta.pageCount}
          totalRows={pageMeta.recordCount}
          currentPage={pageNumber}
          setCurrentPage={(p) => {
            sel.clear();
            setPageNumber(p);
          }}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            sel.clear();
            setPageSize(size);
            setPageNumber(1);
          }}
        />
      </AdminListCard>

      <ConfirmDeleteModal
        open={confirmOpen}
        title={tab === "active" ? "Archive vendors?" : "Restore vendors?"}
        message={`Are you sure you want to ${
          tab === "active" ? "archive" : "restore"
        } ${sel.selectedIds.length} vendor(s)?`}
        confirmLabel={tab === "active" ? "Archive" : "Restore"}
        onConfirm={handleConfirmDeleteOrRestore}
        onCancel={() => setConfirmOpen(false)}
      />

      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default List;
